import type { ClassHomeItem, ClassItem, FileItem, JobItem, User } from "./types";

export interface PromptSuggestion {
  id: string;
  label: string;
  prompt: string;
}

export interface WorkflowCard {
  id: string;
  label: string;
  title: string;
  status: string;
  detail: string;
}

export type FileKind = "image" | "document" | "file";
export const GENERATED_IMAGE_MARKER = /^\[\[generated-image:([^\]]+)\]\]\s*/;

function classContext(classItem: ClassItem | null): string {
  if (!classItem) {
    return "for this classroom";
  }

  return `for ${classItem.name} (${classItem.subject})`;
}

export function describeFileKind(contentType: string): FileKind {
  if (contentType.startsWith("image/")) return "image";
  if (
    contentType.includes("pdf") ||
    contentType.includes("document") ||
    contentType.includes("presentation") ||
    contentType.includes("sheet") ||
    contentType.includes("text/")
  ) {
    return "document";
  }

  return "file";
}

export function buildStarterPrompts(
  classItem: ClassItem | null,
  readyFiles: FileItem[],
): PromptSuggestion[] {
  const sharedContext = classContext(classItem);
  const prompts: PromptSuggestion[] = [
    {
      id: "warm-up",
      label: "Warm-up",
      prompt: `Create a 5-minute warm-up ${sharedContext}. Include one confidence-building question and one stretch question.`,
    },
    {
      id: "explain",
      label: "Explain simply",
      prompt: `Explain today's key idea ${sharedContext} using student-friendly language, one analogy, and three quick checks for understanding.`,
    },
    {
      id: "exit-ticket",
      label: "Exit ticket",
      prompt: `Write a short exit ticket ${sharedContext} with three questions, an answer key, and one teacher follow-up move if students struggle.`,
    },
  ];

  const filePrompts = readyFiles.slice(0, 2).map((file) => {
    const kind = describeFileKind(file.content_type);
    const fileReference = `"${file.filename}"`;

    return {
      id: `file-${file.id}`,
      label: kind === "image" ? `Use image: ${file.filename}` : `Use file: ${file.filename}`,
      prompt:
        kind === "image"
          ? `Draft a notice-and-wonder routine ${sharedContext} that asks students to discuss the uploaded image ${fileReference}. Include one observation prompt, one inference prompt, and one creative extension.`
          : `Draft a short discussion scaffold ${sharedContext} that helps students talk about the uploaded material ${fileReference}. Include a summary prompt, a vocabulary prompt, and two follow-up questions.`,
    };
  });

  return [...prompts, ...filePrompts];
}

export function buildWorkflowCards(input: {
  role: User["role"];
  className: string | null;
  selectedChatTitle: string | null;
  liveStatus: "connecting" | "live" | "offline";
  readyFileCount: number;
  pendingFileCount: number;
  imageFileCount: number;
  selectedChatPendingReply: boolean;
}): WorkflowCard[] {
  const {
    role,
    className,
    selectedChatTitle,
    liveStatus,
    readyFileCount,
    pendingFileCount,
    imageFileCount,
    selectedChatPendingReply,
  } = input;

  const isStudent = role === "STUDENT";
  const hasChat = Boolean(selectedChatTitle);
  const roomStatus = hasChat
    ? liveStatus === "live"
      ? "Live"
      : liveStatus === "connecting"
        ? "Connecting"
        : "Offline"
    : "Choose chat";

  return [
    {
      id: "materials",
      label: "Step 1",
      title: "Stage materials",
      status:
        pendingFileCount > 0
          ? `${pendingFileCount} processing`
          : readyFileCount > 0
            ? `${readyFileCount} ready`
            : "Add materials",
      detail:
        readyFileCount > 0
          ? `${imageFileCount} image-based asset${imageFileCount === 1 ? "" : "s"} staged with the class context.`
          : "Upload images, PDFs, worksheets, or slides so the class has shared material to discuss.",
    },
    {
      id: "thread",
      label: "Step 2",
      title: "Open a live thread",
      status: roomStatus,
      detail: hasChat
        ? `${selectedChatTitle} is the active room for ${className ?? "this class"}.`
        : "Create or select a chat before you send the first classroom prompt.",
    },
    {
      id: "prompt",
      label: "Step 3",
      title: isStudent ? "Ask for support" : "Guide the room",
      status: selectedChatPendingReply ? "Reply pending" : hasChat ? "Ready to prompt" : "Set thread",
      detail: isStudent
        ? "Start with explain, hint, or quiz prompts so the next turn stays focused and age-appropriate."
        : "Launch with a warm-up, model response, or exit ticket instead of writing the first prompt from scratch.",
    },
  ];
}

export function canManageRoster(role: User["role"]): boolean {
  return role !== "STUDENT";
}

export function canUploadMaterials(role: User["role"]): boolean {
  return role !== "STUDENT";
}

export function mergeClassCollections(
  classes: ClassItem[],
  homeClasses: ClassHomeItem[],
): ClassHomeItem[] {
  const merged = new Map<string, ClassHomeItem>();

  for (const classItem of classes) {
    merged.set(classItem.id, {
      ...classItem,
      chat_count: 0,
    });
  }

  for (const homeClass of homeClasses) {
    merged.set(homeClass.id, {
      ...(merged.get(homeClass.id) ?? homeClass),
      ...homeClass,
      chat_count: homeClass.chat_count,
    });
  }

  return [...merged.values()].sort((left, right) =>
    right.updated_at.localeCompare(left.updated_at),
  );
}

export function getPendingAssistantReplyCount(jobs: JobItem[], chatId: string | null): number {
  if (!chatId) return 0;

  return jobs.filter(
    (job) =>
      job.chat_id === chatId &&
      job.type === "assistant-reply" &&
      (job.status === "queued" || job.status === "running"),
  ).length;
}

export function extractGeneratedImage(content: string): {
  imageId: string | null;
  cleanContent: string;
} {
  const match = content.match(GENERATED_IMAGE_MARKER);
  if (!match) {
    return {
      imageId: null,
      cleanContent: content,
    };
  }

  return {
    imageId: match[1] ?? null,
    cleanContent: content.replace(GENERATED_IMAGE_MARKER, "").trim(),
  };
}
